/**
 * The last thing between a render error and a white page.
 *
 * There was no error boundary anywhere in this tree. React's default for an
 * uncaught render error is to unmount the whole application, so any throw --
 * not just the token decode that prompted this -- left the user looking at
 * nothing at all: no message, no navigation, no indication the page had failed
 * rather than simply being slow. The only recovery was clearing site data,
 * which is not something a hiring manager will discover.
 *
 * Guarding the specific decode that caused it is the fix; this is the floor
 * under the next one.
 *
 * WHAT THIS DELIBERATELY DOES NOT DO. It does not guess the cause. The most
 * likely one is a stale or corrupt session, so signing out is offered first --
 * but it is offered as something to TRY, not asserted as the diagnosis. Telling
 * someone "your session expired" when it did not sends them to re-authenticate
 * against a problem that will still be there afterwards.
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { clearSession } from '../../utils/session';
import styles from './index.module.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // No reporting service is wired up in this app. Logging is what there is,
    // and losing the stack entirely would be worse than a console entry.
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info.componentStack);
  }

  private handleSignOut = (): void => {
    clearSession();
    window.location.assign('/auth/login');
  };

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className={styles.wrapper} role="alert">
        <div className={styles.panel}>
          <h1 className={styles.title}>This page stopped working</h1>
          <p className={styles.body}>
            Something failed while rendering, and we do not know what from here.
            Nothing you were looking at has been changed or lost.
          </p>
          <p className={styles.body}>
            Signing out and back in clears the most common cause. If it happens
            again straight away, the problem is on our side rather than with
            your session.
          </p>
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.primary}
              onClick={this.handleSignOut}
            >
              Sign out and sign in again
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={this.handleReload}
            >
              Reload the page
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
